"use client";

import { useCallback, useMemo } from "react";
import { effectiveEmailSchema } from "@/lib/validation/email";
import { checkoutPlanId } from "@/lib/subscription/planCatalog";
import type { BillingInterval } from "@/lib/subscription/planCatalog";
import type { InitPaymentResult } from "./types";
import { usePaymentProvider } from "./usePaymentProvider";

export interface CheckoutSession {
  checkoutUrl: string;
  checkoutId: string;
  amount: number;
  currency: string;
  planId: string;
  displayName: string;
  billingMode?: "recurring" | "prepaid";
  paymentMethod?: "card" | "alipay";
  intentId?: string;
}

/**
 * Validates email and starts checkout via Airwallex (card recurring or Alipay prepaid).
 */
export function useSubscriptionCheckout() {
  const payment = usePaymentProvider();

  const startCheckout = useCallback(
    async (params: {
      email: string;
      tier: string;
      billingInterval: BillingInterval;
      paymentMethod?: "card" | "alipay";
      locale?: string;
    }): Promise<InitPaymentResult & { session?: CheckoutSession }> => {
      const parsed = effectiveEmailSchema.safeParse(params.email);
      if (!parsed.success) {
        return {
          success: false,
          error: parsed.error.issues[0]?.message ?? "Invalid email",
        };
      }

      const planId = checkoutPlanId(
        params.tier as "starter" | "professional" | "enterprise",
        params.billingInterval
      );
      if (!planId) {
        return { success: false, error: "This plan is not available for online checkout." };
      }

      const paymentMethod = params.paymentMethod === "alipay" ? "alipay" : "card";

      try {
        const res = await fetch("/api/checkout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: parsed.data,
            planId,
            paymentMethod,
            locale: params.locale,
          }),
        });

        const data = (await res.json()) as {
          error?: string;
          checkoutUrl?: string;
          checkoutId?: string;
          intentId?: string;
          amount?: number;
          currency?: string;
          planId?: string;
          displayName?: string;
          billingMode?: "recurring" | "prepaid";
          paymentMethod?: "card" | "alipay";
        };

        if (!res.ok) {
          return {
            success: false,
            error: data.error ?? "Payment setup failed",
          };
        }

        if (
          data.checkoutUrl &&
          data.checkoutId &&
          typeof data.amount === "number" &&
          data.currency &&
          data.planId
        ) {
          return {
            success: true,
            redirectUrl: data.checkoutUrl,
            session: {
              checkoutUrl: data.checkoutUrl,
              checkoutId: data.checkoutId,
              amount: data.amount,
              currency: data.currency,
              planId: data.planId,
              displayName: data.displayName ?? data.planId,
              billingMode: data.billingMode,
              paymentMethod: data.paymentMethod ?? paymentMethod,
              intentId: data.intentId,
            },
          };
        }

        return {
          success: false,
          error: data.error ?? "No checkout data received",
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : "Network error";
        return { success: false, error: message };
      }
    },
    []
  );

  return useMemo(
    () => ({ ...payment, startCheckout }),
    [payment, startCheckout]
  );
}
